import React from "react";
import { ExternalLink, Award } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { api } from "../lib/api";

export const trackAndOpen = async (product) => {
    let href = product.href;
    try {
        const { data } = await api.post(`/products/${product.id}/click`);
        if (data?.href) href = data.href;
    } catch (e) { /* open anyway */ }
    window.open(href, "_blank", "noopener,noreferrer");
};

export const ProductsSection = ({ section, products }) => {
    const essentials = products.filter((p) => p.category === "essential");
    return (
        <section id="products" className="py-16 sm:py-20">
            <SectionHeading eyebrow="The toolkit" title={section.title} intro={section.intro} />
            <div className="mt-12 grid gap-6 sm:grid-cols-3">
                {essentials.map((p, i) => (
                    <Reveal key={p.id} delay={i * 0.1} className="h-full">
                        <article
                            data-testid={`product-card-${p.id}`}
                            className={`group relative flex h-full flex-col rounded-[20px] border bg-white p-4 transition-shadow duration-500 hover:shadow-[0_20px_50px_-20px_rgba(34,37,34,0.25)] ${
                                p.recommended ? "border-[#576856] shadow-[0_16px_40px_-20px_rgba(87,104,86,0.45)]" : "border-[#E4DFD3]"
                            }`}
                        >
                            {p.recommended && (
                                <span
                                    data-testid="recommended-badge"
                                    className="absolute -top-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-[#576856] px-3.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow-md"
                                >
                                    <Award size={12} strokeWidth={2} /> Recommended
                                </span>
                            )}
                            <div className="spotlight relative overflow-hidden rounded-xl border border-[#E4DFD3] bg-[#F5F2EA]">
                                <img
                                    src={p.image}
                                    alt={p.name}
                                    loading="lazy"
                                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                />
                                <span className="absolute left-3 top-3 z-[2] rounded-full bg-[#FDFCF8]/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#576856] backdrop-blur-sm">
                                    {p.badge}
                                </span>
                            </div>
                            <h3 className="mt-5 font-serif text-lg font-semibold text-[#1A1D1A]">{p.name}</h3>
                            <p className="mt-1 font-serif italic text-sm text-[#718370]">{p.tagline}</p>
                            <p className="mt-3 flex-1 text-xs sm:text-sm leading-relaxed text-[#6D736D]">{p.description}</p>
                            <div className="mt-5">
                                <a
                                    href={p.href}
                                    target="_blank"
                                    rel="nofollow sponsored noopener"
                                    data-testid={`amazon-buy-btn-${p.id}`}
                                    onClick={(e) => { e.preventDefault(); trackAndOpen(p); }}
                                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[#576856] px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#1A1D1A] active:scale-[0.98]"
                                >
                                    {section.button_label}
                                    <ExternalLink size={14} strokeWidth={1.8} />
                                </a>
                                <p className="mt-2 text-center text-[10px] uppercase tracking-wider text-[#6D736D]/70">{section.microcopy}</p>
                            </div>
                        </article>
                    </Reveal>
                ))}
            </div>
        </section>
    );
};
